import { Button, CircularProgress, FormControl, FormGroup, FormLabel, Stack } from '@mui/material';
import OlSchemaEditor from '@onlive.site/ui/dist/react/schema-editor';
import ActionButton from 'common/components/ActionButton';
import JsonCodeInputMonacoEditor from 'common/components/code-editor/JsonCodeInputMonacoEditor';
import SimpleCodeErrorView from 'common/components/code-editor/SimpleCodeErrorView';
import useFlexBoxStyles from 'common/flexBox';
import { isEmpty } from 'lodash';
import { useSnackbar } from 'notistack';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ValidatorForm } from 'react-material-ui-form-validator';

const FormFormContext = ({ fields, handleFieldChange, step, setStep, cancel, handleError }) => {
    const { t } = useTranslation();
    const { classes: flexBox, cx } = useFlexBoxStyles();
    const [inputError, setInputError] = useState({ context: null });
    const [fetching, setFetching] = useState(false);
    const [editJSON, setEditJSON] = useState(false);
    const [contextSchema, setContextSchema] = useState({});
    const { enqueueSnackbar } = useSnackbar();

    const handleFailFetchingContextSchema = useCallback(() => {
        setFetching(false);
        enqueueSnackbar(t('form.formForm.contextSchema.error'), {
            variant: 'error',
        });
        return {};
    }, [enqueueSnackbar, t]);

    const fetchContextSchema = useCallback(async () => {
        try {
            setFetching(true);
            const optionsSchemaUrl = `${process.env.REACT_APP_ASSETS_SCHEMAS_URL}/staging/FormContext.json`;
            const response = await fetch(optionsSchemaUrl);
            if (!response.ok) {
                return handleFailFetchingContextSchema();
            }

            const schema = await response.json();
            setFetching(false);
            return schema;
        } catch (error) {
            return handleFailFetchingContextSchema();
        }
    }, [handleFailFetchingContextSchema]);

    useEffect(() => {
        fetchContextSchema().then((response) => {
            setContextSchema(response);
        });
    }, [fetchContextSchema, setContextSchema]);

    const handleSubmit = () => {
        setStep(step + 1);
    };

    const handleValueChangeCode = (value, name, errors) => {
        const codeHaveErrors = errors && Array.isArray(errors) && errors.length > 0;
        const newErrors = codeHaveErrors ? errors : null;

        setInputError({ ...inputError, [name]: newErrors });

        handleFieldChange({
            target: {
                name,
                value,
            },
        });
    };

    return (
        <ValidatorForm
            onSubmit={handleSubmit}
            onError={handleError}
            className={cx(flexBox.flex, flexBox.gap2, flexBox.column, flexBox.overflowHidden)}
        >
            <Stack direction="column" spacing={2} className={cx(flexBox.overflowXHidden, flexBox.scrollbarNone)}>
                <FormGroup>
                    <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={2}>
                        <FormLabel>{t('form.formForm.context')}</FormLabel>
                        <Button
                            onClick={() =>
                                setEditJSON(() => {
                                    if (!isEmpty(contextSchema)) {
                                        return !editJSON;
                                    }
                                    return false;
                                })
                            }
                            disabled={isEmpty(contextSchema)}
                        >
                            {editJSON ? 'Form Edit' : 'Edit as JSON'}
                        </Button>
                    </Stack>
                    <FormControl style={{ minHeight: '5em' }}>
                        {fetching && (
                            <Stack
                                direction="row"
                                alignItems="center"
                                justifyContent="center"
                                className={cx(flexBox.zIndex2, flexBox.w100, flexBox.h100, flexBox.absolute)}
                            >
                                <CircularProgress />
                            </Stack>
                        )}
                        {!fetching && !isEmpty(contextSchema) && !editJSON ? (
                            <OlSchemaEditor
                                label="Schema editor"
                                allow-copy
                                data={fields.context || {}}
                                schema={contextSchema}
                                onOlChange={(event) => {
                                    handleFieldChange({ target: { name: 'context', value: event.detail } });
                                }}
                            ></OlSchemaEditor>
                        ) : (
                            <>
                                <JsonCodeInputMonacoEditor
                                    height={fields.context ? 500 : 200}
                                    name="context"
                                    value={fields.context}
                                    onChange={(value, errors) => handleValueChangeCode(value, 'context', errors)}
                                />
                                <SimpleCodeErrorView
                                    id={'error-context'}
                                    mainErrorMessage={t('JsonError')}
                                    errorList={inputError['context']}
                                />
                            </>
                        )}
                    </FormControl>
                </FormGroup>
            </Stack>

            <Stack
                direction="row"
                justifyContent="flex-end"
                alignItems="center"
                spacing={1}
                className={flexBox.paddingTop2}
            >
                <ActionButton onClick={cancel} color="default" fitWidth>
                    {t('app.common.cancel')}
                </ActionButton>

                <ActionButton color="primary" autoFocus fitWidth type={'submit'}>
                    {t('app.common.save')}
                </ActionButton>
            </Stack>
        </ValidatorForm>
    );
};

export default FormFormContext;
